import PhotoSwipeLightbox from "photoswipe/lightbox"
import PhotoSwipe from "photoswipe"
import { LucideIconCache, ChevronLeft, ChevronRight, Printer, X, ZoomIn, ZoomOut } from "./lucide_icon_cache"
import { printImage } from "./print_image"

export interface GalleryImage {
  id: number
  src: string
  width: number
  height: number
  thumb: string
  caption: string
}

export interface LightboxCallbacks {
  onSlideChange?: (index: number) => void
  onDestroy?: () => void
  onUiRegister?: (pswp: PhotoSwipe) => void
}

interface Padding {
  top: number
  bottom: number
  left: number
  right: number
}

/**
 * Get padding values from CSS custom properties.
 * Falls back to defaults if CSS properties are not defined.
 */
function getPaddingFromCSS(): Padding {
  const pswpElement = document.querySelector(".pswp")
  const style = pswpElement ? getComputedStyle(pswpElement) : null

  const parsePixelValue = (value: string, fallback: number): number => {
    if (!value) return fallback
    const parsed = parseInt(value, 10)
    return isNaN(parsed) ? fallback : parsed
  }

  return {
    top: parsePixelValue(style?.getPropertyValue("--pswp-padding-top") ?? "", 114),
    bottom: parsePixelValue(style?.getPropertyValue("--pswp-padding-bottom") ?? "", 80),
    left: parsePixelValue(style?.getPropertyValue("--pswp-padding-x") ?? "", 24),
    right: parsePixelValue(style?.getPropertyValue("--pswp-padding-x") ?? "", 24),
  }
}

/**
 * Add ARIA attributes to PhotoSwipe elements for accessibility.
 * PhotoSwipe handles most accessibility automatically (role="dialog", button labels via title options).
 * We only add attributes that PhotoSwipe doesn't provide natively.
 */
function addAccessibilityAttributes(pswp: PhotoSwipe): void {
  if (!pswp.element) return

  // PhotoSwipe sets role="dialog" automatically but we add additional context
  pswp.element.setAttribute("aria-label", "Image gallery")
  pswp.element.setAttribute("aria-modal", "true")
}

/**
 * Register print button in PhotoSwipe toolbar.
 */
function registerPrintButton(pswp: PhotoSwipe): void {
  pswp.ui?.registerElement({
    name: "print-button",
    order: 8,
    isButton: true,
    ariaLabel: "Print image",
    html: LucideIconCache.get(Printer, 20, "pswp__icn"),
    onClick: () => {
      const src = pswp.currSlide?.data.src
      if (src) {
        printImage(src)
      }
    },
  })
}

/**
 * Register custom close button with Lucide X icon.
 * Replaces PhotoSwipe's default close button for consistent icon styling.
 */
function registerCloseButton(pswp: PhotoSwipe): void {
  pswp.ui?.registerElement({
    name: "close-button",
    order: 20,
    isButton: true,
    ariaLabel: "Close gallery",
    html: LucideIconCache.get(X, 20, "pswp__icn"),
    onClick: () => {
      pswp.close()
    },
  })
}

/**
 * Register custom zoom button with Lucide zoom icons.
 * Replaces PhotoSwipe's default zoom button for consistent icon styling.
 * Shows ZoomIn when not zoomed, ZoomOut when zoomed.
 */
function registerZoomButton(pswp: PhotoSwipe): void {
  pswp.ui?.registerElement({
    name: "zoom-button",
    order: 10,
    isButton: true,
    ariaLabel: "Toggle zoom",
    html: LucideIconCache.get(ZoomIn, 20, "pswp__icn"),
    onInit: (el) => {
      const updateIcon = () => {
        const isZoomed = pswp.currSlide?.currZoomLevel !== pswp.currSlide?.zoomLevels.initial
        el.innerHTML = isZoomed
          ? LucideIconCache.get(ZoomOut, 20, "pswp__icn")
          : LucideIconCache.get(ZoomIn, 20, "pswp__icn")
      }

      // Update icon when zoom level changes
      pswp.on("zoomPanUpdate", updateIcon)
      pswp.on("change", updateIcon)
    },
    onClick: () => {
      pswp.toggleZoom()
    },
  })
}

/**
 * Register top bar info element with counter and caption.
 * Uses proper HTML structure instead of CSS position hacks.
 */
function registerTopBarInfo(pswp: PhotoSwipe, images: GalleryImage[]): void {
  pswp.ui?.registerElement({
    name: "top-bar-info",
    // appendTo: 'bar' is default - element participates in top bar flex layout
    order: 5,
    onInit: (el) => {
      el.className = "pswp__top-bar-info"

      // Create counter element
      const counterEl = document.createElement("div")
      counterEl.className = "pswp__counter-text"
      el.appendChild(counterEl)

      // Create caption element as a status region for screen readers
      const captionEl = document.createElement("div")
      captionEl.className = "pswp__caption-text"
      captionEl.setAttribute("role", "status")
      captionEl.setAttribute("aria-label", "Image details")
      el.appendChild(captionEl)

      const update = () => {
        const current = (pswp.currIndex ?? 0) + 1
        const total = pswp.getNumItems()
        counterEl.textContent = `Image ${current} of ${total}`

        const image = images[pswp.currIndex]
        if (image?.caption) {
          captionEl.textContent = image.caption
          captionEl.classList.remove("pswp__caption-text--empty")
        } else {
          captionEl.textContent = ""
          captionEl.classList.add("pswp__caption-text--empty")
        }
      }

      update()
      pswp.on("change", update)
    },
  })
}

/**
 * GalleryLightbox wraps PhotoSwipe lightbox with standard configuration
 * and provides a simplified API for the gallery controller.
 */
export class GalleryLightbox {
  private lightbox: PhotoSwipeLightbox | null = null
  private images: GalleryImage[]
  private callbacks: LightboxCallbacks

  constructor(images: GalleryImage[], callbacks: LightboxCallbacks = {}) {
    this.images = images
    this.callbacks = callbacks
  }

  /**
   * Open the lightbox at the specified index.
   */
  open(startIndex: number): void {
    this.lightbox = new PhotoSwipeLightbox({
      dataSource: this.images,
      index: startIndex,
      bgOpacity: 0.97,
      showHideAnimationType: "fade",
      pswpModule: PhotoSwipe,
      // Padding around the image - reads from CSS custom properties for consistency
      paddingFn: () => getPaddingFromCSS(),
      // Show thumbnail as placeholder while full image loads
      showHideOpacity: true,
      // Show loading indicator immediately (default is 2000ms which feels slow)
      preloaderDelay: 0,
      // Custom arrow SVGs using Lucide chevron icons for consistency
      // These replace PhotoSwipe's default filled arrow icons with stroke-based chevrons
      // Icon size fs-5 (20px) per design guide for icon-only buttons
      arrowPrevSVG: LucideIconCache.get(ChevronLeft, 20),
      arrowNextSVG: LucideIconCache.get(ChevronRight, 20),
      // Accessibility labels for built-in buttons
      closeTitle: "Close gallery",
      zoomTitle: "Toggle zoom",
      arrowPrevTitle: "Previous image",
      arrowNextTitle: "Next image",
    })

    // Register custom UI elements
    this.lightbox.on("uiRegister", () => {
      if (!this.lightbox?.pswp) return
      registerTopBarInfo(this.lightbox.pswp, this.images)
      registerPrintButton(this.lightbox.pswp)
      registerZoomButton(this.lightbox.pswp)
      registerCloseButton(this.lightbox.pswp)
      this.callbacks.onUiRegister?.(this.lightbox.pswp)
    })

    // Add accessibility attributes when lightbox opens
    this.lightbox.on("openingAnimationEnd", () => {
      if (this.lightbox?.pswp) {
        addAccessibilityAttributes(this.lightbox.pswp)
      }
    })

    // Notify on slide change
    this.lightbox.on("change", () => {
      if (this.lightbox?.pswp) {
        this.callbacks.onSlideChange?.(this.lightbox.pswp.currIndex)
      }
    })

    // Clean up when lightbox closes
    this.lightbox.on("destroy", () => {
      this.callbacks.onDestroy?.()
      this.lightbox = null
    })

    this.lightbox.init()
    this.lightbox.loadAndOpen(startIndex)
  }

  /**
   * Get the current slide index.
   */
  getCurrentIndex(): number {
    return this.lightbox?.pswp?.currIndex ?? 0
  }

  /**
   * Navigate to a specific slide.
   */
  goTo(index: number): void {
    this.lightbox?.pswp?.goTo(index)
  }

  /**
   * Get the PhotoSwipe instance for advanced use cases.
   */
  getPswp(): PhotoSwipe | undefined {
    return this.lightbox?.pswp
  }

  /**
   * Destroy the lightbox and clean up resources.
   */
  destroy(): void {
    if (this.lightbox) {
      this.lightbox.destroy()
      this.lightbox = null
    }
  }
}
